import { CskPageOptions } from "src/utils/create-get-static-props";

/** How the story presents subscribe/nav chrome. */
export type StoryNavMode = "fixed" | "reveal-after-lede" | "hidden";

/** Story-level light/dark theme and nav toggles (see `config/constants`). */
export interface StoryThemeConfig {
  /** Theme-contract colors + default `pageTheme` (`vergeLight`). */
  enabled: boolean;
  /** Nav drawer light/dark switcher (requires `minimalNav` and fixed nav). */
  drawerSwitcher: boolean;
  /** Fixed minimal nav + subscribe overlay (`navMode: "fixed"`). */
  minimalNav: boolean;
  /**
   * Nav presentation. Defaults to `"fixed"` when `minimalNav` is true, otherwise `"hidden"`.
   * Use `"reveal-after-lede"` for scroll-reveal subscribe nav (gpus-style).
   */
  navMode?: StoryNavMode;
}

export interface StorySectionsConfig {
  credits: boolean;
}

export interface StoryAdsConfig {
  paragraphRunLength: number;
}

/** Shared story feature toggles referenced from each story's `config/constants.ts`. */
export interface StoryConfig {
  storyTheme: StoryThemeConfig;
  sections: StorySectionsConfig;
  refreshLiveMetadata: boolean;
  ads: StoryAdsConfig;
}

export function resolveStoryNavMode(
  storyTheme: StoryThemeConfig,
): StoryNavMode {
  if (storyTheme.navMode) {
    return storyTheme.navMode;
  }
  return storyTheme.minimalNav ? "fixed" : "hidden";
}

/** Maps `storyTheme` config to CSK `pageOptions` for `getStaticProps`. */
export function getStoryThemePageOptions(
  storyTheme: StoryThemeConfig,
  overrides: CskPageOptions = {},
): CskPageOptions {
  const navMode = resolveStoryNavMode(storyTheme);

  if (navMode === "reveal-after-lede" || navMode === "hidden") {
    return {
      hideNav: true,
      ...(storyTheme.enabled ? { pageTheme: "vergeLight" } : {}),
      ...(storyTheme.drawerSwitcher && storyTheme.minimalNav
        ? { drawerThemeSwitcher: true }
        : {}),
      ...overrides,
    };
  }

  const themeOptions: CskPageOptions = storyTheme.enabled
    ? {
        ...(storyTheme.drawerSwitcher && storyTheme.minimalNav
          ? {}
          : { pageTheme: "vergeLight" }),
        drawerThemeSwitcher: storyTheme.drawerSwitcher && storyTheme.minimalNav,
        hideNav: true,
      }
    : {
        hideNav: true,
      };

  return { ...themeOptions, ...overrides };
}
